import React from "react";
import { useDropzone } from "react-dropzone";
import { Edit, Timeline } from "@mui/icons-material";
import { Box, Paper } from "@mui/material";
import { useSnackbar } from "notistack";

import { getAllAcceptedExtensions, validateFilePackage } from "fond/fileValidation";
import { addImport } from "fond/redux/imports";
import { MuPowerEditableLayerIds } from "fond/types";
import { getFilesFromEvent } from "fond/utils/files";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { LinearProgressWithLabel, StackedNavigationButton } from "fond/widgets";

interface DroppableNavButtonProps {
  icon: React.ReactNode;
  title: string;
  target: string;
  layerKey: string;
  disabled?: boolean;
}

const DroppableNavButton: React.FC<DroppableNavButtonProps> = ({ icon, title, target, layerKey, disabled }) => {
  const dispatch = useAppDispatch();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { enqueueSnackbar } = useSnackbar();

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: getAllAcceptedExtensions(),
    getFilesFromEvent,
    disabled,
    onDropAccepted: (files) => {
      const { isPackageValid, error } = validateFilePackage(files);
      if (isPackageValid) {
        const baseName = files[0]?.name.split(".")[0].toLowerCase() || layerKey.split("/").pop() || "layer";
        dispatch(addImport({ versionId, layerKey, displayName: baseName, files }));
      } else {
        enqueueSnackbar(error?.message || "Invalid file package", { variant: "error" });
      }
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        borderRadius: 1,
        transition: "outline 0.15s",
        outline: isDragActive ? "2px dashed" : "2px dashed transparent",
        outlineColor: isDragActive ? "primary.main" : "transparent",
      }}
    >
      <StackedNavigationButton icon={icon} title={title} target={target} sx={{ m: 0, mt: 1 }} disabled={disabled} />
    </Box>
  );
};

interface ModificationsPanelProps {
  isUploading?: boolean;
}

const ModificationsPanel: React.FC<ModificationsPanelProps> = ({ isUploading }) => {
  return (
    <>
      <DroppableNavButton
        icon={<Edit />}
        title="Edit Underground"
        target="modification-muPowerUnderground"
        layerKey={MuPowerEditableLayerIds.ugPath}
        disabled={isUploading}
      />
      <DroppableNavButton
        icon={<Timeline />}
        title="Edit Leadins"
        target="modification-muPowerLeadins"
        layerKey={MuPowerEditableLayerIds.leadins}
        disabled={isUploading}
      />

      {isUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}
    </>
  );
};

export default ModificationsPanel;
